import { makeAutoObservable, runInAction, reaction } from "mobx";
import { Bid } from "types/common/ServiceStoreTypes";
import { BidFormData } from "../../../types/Bid.types";
import { RootStore, ServiceStore } from "@intrasync/stores";

export interface BidFormState {
  formData: BidFormData;
  error: string | null;
  validationErrors: Record<string, string>;
  isEditMode: boolean;
  bidId: number | null;
}

export class BidFormStore {
  rootStore: RootStore;
  private disposers: Array<() => void> = [];

  // Observables - Only form UI state
  formData: BidFormData = {
    bidNumber: "",
    status: "Draft",
    opportunityId: undefined,
    bidAmount: undefined,
    submissionDate: "",
    validUntilDate: "",
  };
  error: string | null = null;
  validationErrors: Record<string, string> = {};
  isEditMode = false;
  bidId: number | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false,
    });

    // Auto-generate bid number when OpportunityId changes
    this.disposers.push(
      reaction(
        () => this.formData.opportunityId,
        () => {
          if (!this.isEditMode) {
            this.generateBidNumber();
          }
        }
      )
    );

    // Generate initial bid number
    this.generateBidNumber();
  }

  // Computed values
  get bidsStore(): ServiceStore {
    return this.rootStore.bidsStore as ServiceStore;
  }

  get originalBid(): Bid | null {
    return this.bidId
      ? (this.bidsStore.entities.find(
          (b: any) => b.id === this.bidId
        ) as unknown as Bid)
      : null;
  }

  get isLoading(): boolean {
    return this.bidsStore.isServicePending;
  }

  get isSaving(): boolean {
    return this.bidsStore.isServicePending;
  }

  // Generate bid number using formula: BID-{OpportunityId or GENERAL}-{timestamp}
  generateBidNumber() {
    const opportunityPart = this.formData.opportunityId
      ? this.formData.opportunityId.toString()
      : "GENERAL";
    const timestamp = Date.now().toString().slice(-8); // Last 8 digits of timestamp
    const bidNumber = `BID-${opportunityPart}-${timestamp}`;

    runInAction(() => {
      this.formData.bidNumber = bidNumber;
    });
  }

  // Data loading for edit mode
  async loadBid(bidId: number) {
    this.error = null;
    this.isEditMode = true;
    this.bidId = bidId;

    try {
      console.log("Loading bid for edit:", bidId);
      const bid = (await this.bidsStore.get(bidId)) as unknown as Bid;

      runInAction(() => {
        this.formData = {
          bidNumber: bid.bidNumber,
          opportunityId: bid.opportunityId,
          status: bid.status,
          bidAmount: bid.bidAmount,
          submissionDate: bid.submissionDate,
          validUntilDate: bid.validUntilDate,
        };
      });
    } catch (error) {
      console.error("Failed to load bid:", error);
      runInAction(() => {
        this.error = "Failed to load bid for editing. Please try again.";
      });
      throw error;
    }
  }

  // Form field updates
  updateField = <K extends keyof BidFormData>(
    field: K,
    value: BidFormData[K]
  ) => {
    runInAction(() => {
      this.formData[field] = value;
      console.log("FormData updated:", { ...this.formData });

      // Clear field-specific validation error
      if (this.validationErrors[field]) {
        delete this.validationErrors[field];
      }
    });
  };

  // Validation
  validateForm(): boolean {
    console.log("FormData validateForm():", { ...this.formData });
    const errors: Record<string, string> = {};

    // Required fields (BidNumber is auto-generated, so not validated)
    if (!this.formData.status || !this.formData.status.trim()) {
      errors.status = "Status is required";
    }

    // Amount validation
    if (
      this.formData.bidAmount !== undefined &&
      Number(this.formData.bidAmount) <= 0
    ) {
      errors.bidAmount = "Bid amount must be greater than 0";
    }

    // Date validation
    if (this.formData.validUntilDate && this.formData.submissionDate) {
      const validUntilDate = new Date(this.formData.validUntilDate);
      const submissionDate = new Date(this.formData.submissionDate);

      if (validUntilDate <= submissionDate) {
        errors.validUntilDate =
          "Valid until date must be after submission date";
      }
    }

    runInAction(() => {
      this.validationErrors = errors;
    });

    console.log("Validation errors:", errors);

    return Object.keys(errors).length === 0;
  }

  // Save operations
  async saveBid(): Promise<boolean> {
    if (!this.validateForm()) {
      console.warn("Form validation failed:", this.validationErrors);
      return false;
    }

    this.error = null;

    try {
      if (this.isEditMode && this.bidId) {
        // Update existing bid
        console.log("Updating bid:", this.bidId, this.formData);
        await this.bidsStore.patch(
          this.bidId,
          this.formData as unknown as Parameters<typeof this.bidsStore.patch>[1]
        );
      } else {
        // Create new bid
        console.log("Creating new bid:", this.formData);
        await this.bidsStore.create({
          ...this.formData,
          BidAmount:
            "BidAmount" in this.formData
              ? parseInt(String(this.formData.bidAmount))
              : 0,
        });
      }

      return true;
    } catch (error) {
      console.error("Failed to save bid:", error);
      runInAction(() => {
        this.error = "Failed to save bid. Please try again.";
      });
      throw error;
    }
  }

  // Reset form
  resetForm() {
    runInAction(() => {
      this.formData = {
        bidNumber: "",
        status: "Draft",
        opportunityId: undefined,
        bidAmount: undefined,
        submissionDate: "",
        validUntilDate: "",
      };
      this.validationErrors = {};
      this.error = null;
      this.isEditMode = false;
      this.bidId = null;
    });

    // Generate new bid number after reset
    this.generateBidNumber();
  }

  // Computed properties
  get hasChanges(): boolean {
    if (!this.isEditMode || !this.originalBid) {
      return Object.values(this.formData).some(
        (value: any) => value !== "" && value !== undefined && value !== null
      );
    }

    // Compare with original bid data
    return Object.keys(this.formData).some((key: any) => {
      const formValue = this.formData[key as keyof BidFormData];
      const originalValue = this.originalBid![key as keyof Bid];
      return formValue !== originalValue;
    });
  }

  get isValid(): boolean {
    return (
      Object.keys(this.validationErrors).length === 0 &&
      this.formData.bidNumber &&
      this.formData.bidNumber.trim() !== "" &&
      this.formData.status &&
      this.formData.status.trim() !== ""
    );
  }

  // Available options for form fields
  get availableOpportunities(): Array<{ id: number; name: string }> {
    const opportunitiesStore = this.rootStore
      .opportunitiesStore as ServiceStore;
    return opportunitiesStore.entities.map((opportunity: unknown) => {
      const oppData = opportunity as {
        opportunityId: number;
        opportunityName: string;
      };
      return {
        id: oppData.opportunityId,
        name: oppData.opportunityName || `Opportunity ${oppData.opportunityId}`,
      };
    });
  }

  get availableStatuses(): string[] {
    return [
      "Draft",
      "Submitted",
      "Under Review",
      "Awarded",
      "Lost",
      "Cancelled",
    ];
  }

  get nextBidNumber(): string {
    // Generate a new bid number by incrementing the last bid number in the format BID-2024-001
    const bidsStore = this.rootStore.bidsStore as ServiceStore;
    const bids = bidsStore.entities as unknown as Bid[];
    const lastBid = bids
      .sort((a, b) => (a.bidId > b.bidId ? -1 : 1))
      .find((bid: any) => !!bid.bidNumber);

    console.log("Last bid found:", lastBid);

    return lastBid
      ? (parseInt(lastBid?.bidNumber.split("-").pop() || "0") + 1)
          .toString()
          .padStart(3, "0")
      : "001";
  }

  // Clear state
  clear() {
    this.resetForm();
  }

  // Dispose reactions to prevent memory leaks
  dispose() {
    this.disposers.forEach((dispose: any) => dispose());
    this.disposers.length = 0;
  }
}

export default BidFormStore;
